import { useState, useRef } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Shield,
  ContactSupport,
  WhatsApp,
  MailOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { authAPI } from "../../services/api";
import { SessionManager } from "../../utils/sessionManager";

const SKY = "#7ec8e3";
const SKY_DARK = "#1E9CF0";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [ipVerificationRequired, setIpVerificationRequired] = useState(false);
  const [ipAddress, setIpAddress] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [verifying, setVerifying] = useState(false);

  // Forgot password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(0); // 0=email, 1=otp, 2=new password
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState(["", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [touched, setTouched] = useState({ email: false, password: false });

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const forgotOtpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const handleForgotOpen = () => {
    setForgotOpen(true);
    setForgotStep(0);
    setForgotEmail("");
    setForgotOtp(["", "", "", ""]);
    setNewPassword("");
    setConfirmPassword("");
    setForgotError("");
  };

  const handleForgotClose = () => {
    setForgotOpen(false);
    setForgotError("");
  };

  const handleSendResetOTP = async () => {
    if (!forgotEmail) {
      setForgotError("Please enter your email");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    try {
      await authAPI.sendPasswordResetOTP({ email: forgotEmail });
      toast.success("OTP sent to your email");
      setForgotStep(1);
    } catch (err) {
      setForgotError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyResetOTP = async () => {
    const otpString = forgotOtp.join("");
    if (otpString.length !== 4) {
      setForgotError("Please enter a valid 4-digit OTP");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    try {
      await authAPI.verifyPasswordResetOTP({ email: forgotEmail, otp: otpString });
      toast.success("OTP verified");
      setForgotStep(2);
    } catch (err) {
      setForgotError(err.response?.data?.message || "Invalid OTP");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setForgotError("Please fill in both fields");
      return;
    }
    if (newPassword.length < 6) {
      setForgotError("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setForgotError("Passwords do not match");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    try {
      await authAPI.resetPassword({ email: forgotEmail, newPassword });
      toast.success("Password reset successfully! Please sign in.");
      handleForgotClose();
    } catch (err) {
      setForgotError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotOTPChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...forgotOtp];
    newOtp[index] = value;
    setForgotOtp(newOtp);
    if (value && index < 3) forgotOtpRefs[index + 1].current?.focus();
  };

  const handleForgotOTPKeyDown = (index, e) => {
    if (e.key === "Backspace" && !forgotOtp[index] && index > 0)
      forgotOtpRefs[index - 1].current?.focus();
  };

  const validateEmail = (value) => {
    if (!value) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return "";
  };

  const validatePassword = (value) => {
    if (!value) return "Password is required";
    return "";
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) setEmailError(validateEmail(value));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) setPasswordError(validatePassword(value));
  };

  const handleEmailBlur = () => {
    setTouched({ ...touched, email: true });
    setEmailError(validateEmail(email));
  };

  const handlePasswordBlur = () => {
    setTouched({ ...touched, password: true });
    setPasswordError(validatePassword(password));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);
    setEmailError(emailErr);
    setPasswordError(passwordErr);
    setTouched({ email: true, password: true });
    if (emailErr || passwordErr) return;

    setLoading(true);
    setError("");
    try {
      const response = await authAPI.login(email, password);
      if (response.requiresIPVerification) {
        setIpAddress(response.ipAddress);
        setIpVerificationRequired(true);
        toast.info(response.message);
      } else {
        SessionManager.createSession(response.data.token, response.data.user);
        navigate(response.data.user.role === "Admin" ? "/admin" : "/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) otpRefs[index + 1].current?.focus();
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      otpRefs[index - 1].current?.focus();
  };

  const handleVerifyIP = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }
    setVerifying(true);
    try {
      const response = await authAPI.verifyIPOTP(email, otpString, ipAddress);
      if (response.success) {
        toast.success("Device verified successfully!");
        SessionManager.createSession(response.data.token, response.data.user);
        navigate(response.data.user.role === "Admin" ? "/admin" : "/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResendIPOTP = async () => {
    setLoading(true);
    try {
      const response = await authAPI.login(email, password);
      if (response.requiresIPVerification) {
        setOtp(["", "", "", ""]);
        toast.success("New OTP sent to your email");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseIPDialog = () => {
    setIpVerificationRequired(false);
    setOtp(["", "", "", ""]);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: "#f7f8fa",
      fontSize: { xs: "0.85rem", sm: "0.9rem" },
      transition: "all 0.25s ease-in-out",
      "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
      "&:hover": {
        backgroundColor: "#fff",
        "& fieldset": { borderColor: SKY_DARK },
        boxShadow: `0 4px 14px rgba(91,184,212,0.10)`,
      },
      "&.Mui-focused": {
        backgroundColor: "#fff",
        "& fieldset": { borderWidth: "2px", borderColor: SKY },
        boxShadow: `0 4px 18px rgba(126,200,227,0.18)`,
      },
      "& input::-ms-reveal, & input::-ms-clear": { display: "none" },
    },
    "& .MuiInputLabel-root": {
      fontSize: { xs: "0.8rem", sm: "0.85rem" },
      color: "#6b7280",
      "&.Mui-focused": { color: SKY_DARK, fontWeight: 600 },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f4f6f9",
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      {/* Floating Contact Button */}
      <Box
        sx={{
          position: "fixed",
          top: { xs: 12, sm: 20 },
          right: { xs: 12, sm: 24 },
          zIndex: 1200,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0,
        }}
      >
        <Button
          onClick={() => setContactOpen(!contactOpen)}
          variant="contained"
          startIcon={<ContactSupport sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }} />}
          endIcon={
            contactOpen ? (
              <KeyboardArrowUp sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }} />
            ) : (
              <KeyboardArrowDown sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }} />
            )
          }
          sx={{
            background: "#2c3e50",
            borderRadius: contactOpen ? "14px 14px 0 0" : "14px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "0.72rem", sm: "0.8rem" },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.7, sm: 0.85 },
            boxShadow: "0 4px 20px rgba(44,62,80,0.25)",
            transition: "all 0.2s ease",
            letterSpacing: "0.01em",
            minWidth: 0,
            "&:hover": {
              background: "#1a252f",
              boxShadow: "0 6px 24px rgba(44,62,80,0.35)",
            },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Contact Us</Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Help</Box>
        </Button>

        <Collapse in={contactOpen} timeout={250}>
          <Box
            sx={{
              background: "#1a252f",
              borderRadius: "0 0 14px 14px",
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(44,62,80,0.3)",
              minWidth: { xs: 160, sm: 180 },
            }}
          >
            {/* Email */}
            <Box
              component="a"
              href="mailto:office@deepxtechnologies.com"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1.2, sm: 1.4 },
                textDecoration: "none",
                cursor: "pointer",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                transition: "background 0.18s ease",
                "&:hover": { background: "rgba(255,255,255,0.07)" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 26, sm: 30 },
                  height: { xs: 26, sm: 30 },
                  borderRadius: "8px",
                  bgcolor: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <MailOutline sx={{ color: SKY, fontSize: { xs: "0.85rem", sm: "1rem" } }} />
              </Box>
              <Box sx={{ overflow: "hidden" }}>
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.65rem", lineHeight: 1 }}>
                  Email Support
                </Typography>
                <Typography sx={{ color: "white", fontSize: { xs: "0.68rem", sm: "0.78rem" }, fontWeight: 600, mt: 0.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  office@deepxtechnologies.com
                </Typography>
              </Box>
            </Box>

            {/* WhatsApp - Sales */}
            <Box
              component="a"
              href="https://wa.me/919770719703?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20KYC%20Suite%20plans%20and%20pricing.%20Could%20you%20share%20the%20details%3F"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1.2, sm: 1.4 },
                textDecoration: "none",
                cursor: "pointer",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                transition: "background 0.18s ease",
                "&:hover": { background: "rgba(255,255,255,0.07)" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 26, sm: 30 },
                  height: { xs: 26, sm: 30 },
                  borderRadius: "8px",
                  bgcolor: "rgba(37,211,102,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <WhatsApp sx={{ color: "#25d366", fontSize: { xs: "0.85rem", sm: "1rem" } }} />
              </Box>
              <Box>
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.65rem", lineHeight: 1 }}>
                  WhatsApp (Sales)
                </Typography>
                <Typography sx={{ color: "white", fontSize: { xs: "0.68rem", sm: "0.78rem" }, fontWeight: 600, mt: 0.2 }}>
                  +91 977 071 9703
                </Typography>
              </Box>
            </Box>

            {/* WhatsApp - Support */}
            <Box
              component="a"
              href="https://wa.me/919307596330?text=Hi%2C%20I%20need%20help%20with%20my%20KYC%20Suite%20account.%20Could%20you%20please%20assist%20me%3F"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1.2, sm: 1.4 },
                textDecoration: "none",
                cursor: "pointer",
                transition: "background 0.18s ease",
                "&:hover": { background: "rgba(255,255,255,0.07)" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 26, sm: 30 },
                  height: { xs: 26, sm: 30 },
                  borderRadius: "8px",
                  bgcolor: "rgba(37,211,102,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <WhatsApp sx={{ color: "#25d366", fontSize: { xs: "0.85rem", sm: "1rem" } }} />
              </Box>
              <Box>
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.65rem", lineHeight: 1 }}>
                  WhatsApp (Support)
                </Typography>
                <Typography sx={{ color: "white", fontSize: { xs: "0.68rem", sm: "0.78rem" }, fontWeight: 600, mt: 0.2 }}>
                  +91 930 759 6330
                </Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Main Content */}
      <Grid container sx={{ flex: 1, minHeight: "100vh" }}>

        {/* Left — Form Panel */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "center",
            p: { xs: "72px 16px 32px", sm: "80px 32px 40px", md: 6 },
            minHeight: { xs: "100vh", md: "auto" },
            background: "#f4f6f9",
          }}
        >
          <Box sx={{ maxWidth: 440, width: "100%" }}>

            {/* Mobile Brand */}
            <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: { xs: 3, sm: 5 }, justifyContent: "center" }}>
              <Shield sx={{ color: "#2c3e50", fontSize: { xs: "1.6rem", sm: "2rem" } }} />
              <Typography sx={{ fontWeight: 900, color: "#2c3e50", fontSize: { xs: "1.6rem", sm: "2rem" }, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>
                KYC SUITE
              </Typography>
            </Box>

            {/* Heading */}
            <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
              <Typography sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: { xs: "1.45rem", sm: "1.7rem", md: "2rem" }, lineHeight: 1.15, mb: 0.75, letterSpacing: "-0.02em" }}>
                Welcome
              </Typography>
              <Typography sx={{ color: "#6b7280", fontSize: { xs: "0.82rem", sm: "0.9rem" }, fontWeight: 400 }}>
                Sign in to your KYC Suite account
              </Typography>
            </Box>

            {/* Form Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3, md: 4 },
                borderRadius: "20px",
                bgcolor: "white",
                boxShadow: "0 2px 32px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <Box component="form" onSubmit={handleSubmit} autoComplete="off">

                {/* Email */}
                <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
                  <Typography sx={{ color: "#374151", fontWeight: 600, fontSize: { xs: "0.72rem", sm: "0.78rem" }, mb: 0.75, display: "block", letterSpacing: "0.03em", textTransform: "uppercase" }}>
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="you@company.com"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    error={touched.email && !!emailError}
                    helperText={touched.email && emailError}
                    autoComplete="off"
                    size="small"
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ fontSize: { xs: "0.95rem", sm: "1.05rem" }, color: touched.email && emailError ? "error.main" : SKY_DARK }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Password */}
                <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                    <Typography sx={{ color: "#374151", fontWeight: 600, fontSize: { xs: "0.72rem", sm: "0.78rem" }, letterSpacing: "0.03em", textTransform: "uppercase" }}>
                      Password
                    </Typography>
                    <Link onClick={handleForgotOpen} sx={{ textDecoration: "none", color: SKY_DARK, fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.75rem" }, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                      Forgot password?
                    </Link>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={handlePasswordBlur}
                    error={touched.password && !!passwordError}
                    helperText={touched.password && passwordError}
                    autoComplete="new-password"
                    size="small"
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ fontSize: { xs: "0.95rem", sm: "1.05rem" }, color: touched.password && passwordError ? "error.main" : SKY_DARK }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" tabIndex={-1} size="small" sx={{ color: "#9ca3af" }}>
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2.5, borderRadius: "12px", fontSize: { xs: "0.78rem", sm: "0.83rem" } }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: { xs: 1, sm: 1.5 },
                    py: { xs: 1.2, sm: 1.45 },
                    fontSize: { xs: "0.88rem", sm: "0.95rem" },
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "14px",
                    background: `linear-gradient(135deg, #2c3e50 0%, ${SKY_DARK} 100%)`,
                    boxShadow: `0 4px 18px rgba(44,62,80,0.28)`,
                    letterSpacing: "0.02em",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      background: `linear-gradient(135deg, #1a252f 0%, ${SKY_DARK} 100%)`,
                      boxShadow: `0 8px 24px rgba(91,184,212,0.32)`,
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": { background: "#c5cae9" },
                  }}
                >
                  {loading ? "Signing In…" : "Sign In →"}
                </Button>

                <Divider sx={{ my: { xs: 2, sm: 2.5 } }}>
                  <Typography sx={{ color: "#9ca3af", fontSize: "0.75rem", px: 1 }}>OR</Typography>
                </Divider>

                <Typography align="center" sx={{ color: "#6b7280", fontSize: { xs: "0.82rem", sm: "0.875rem" } }}>
                  New to the platform?{" "}
                  <Link component={RouterLink} to="/register" sx={{ fontWeight: 700, textDecoration: "none", color: SKY_DARK, "&:hover": { textDecoration: "underline" } }}>
                    Create an Account
                  </Link>
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* Right — Branding Panel */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            background: "linear-gradient(150deg, #1e3a4f 0%, #3b7096ff 50%, #3d83afff 100%)",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: { md: 5, lg: 7 },
            position: "relative",
            overflow: "hidden",
            minHeight: "100vh",
          }}
        >
          {/* Subtle grid texture */}
          <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

          {/* Glow orbs */}
          <Box sx={{ position: "absolute", top: "8%", right: "4%", width: 280, height: 280, borderRadius: "50%", background: `radial-gradient(circle, rgba(126,210,240,0.18) 0%, transparent 70%)` }} />
          <Box sx={{ position: "absolute", bottom: "6%", left: "-2%", width: 340, height: 340, borderRadius: "50%", background: `radial-gradient(circle, rgba(126,210,240,0.12) 0%, transparent 70%)` }} />

          <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", width: "100%", maxWidth: 480 }}>
            {/* KYC SUITE Brand */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mb: 1.25 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: "16px",
                background: `rgba(126,210,240,0.18)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: `1px solid rgba(126,210,240,0.35)`,
                flexShrink: 0,
              }}>
                <Shield sx={{ color: SKY, fontSize: "1.7rem" }} />
              </Box>
              <Typography sx={{
                fontWeight: 900, color: "white", fontSize: "2.5rem",
                letterSpacing: "0.15em", textTransform: "uppercase",
                lineHeight: 1, fontFamily: '"Helvetica Neue", Arial, sans-serif',
              }}>
                KYC SUITE
              </Typography>
            </Box>

            {/* Tagline */}
            <Typography sx={{ color: SKY, opacity: 0.85, fontSize: "0.78rem", letterSpacing: "0.1em", mb: 1.5, textTransform: "uppercase", fontWeight: 500 }}>
              Streamlined Verification Solutions
            </Typography>

            {/* Decorative rule */}
            <Box sx={{ width: 60, height: 2, background: `linear-gradient(90deg, transparent, ${SKY}, transparent)`, borderRadius: 2, mx: "auto", mb: 1.5 }} />

            {/* Illustration */}
            <img
              src="/KYC-2.png"
              alt="Verification"
              style={{
                width: "100%",
                height: "420px",
                objectFit: "contain",
                borderRadius: 14,
                display: "block",
                margin: "0 auto 16px",
                filter: "brightness(1.05) saturate(1.1)",
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* IP Verification Dialog */}
      <Dialog
        open={ipVerificationRequired}
        onClose={handleCloseIPDialog}
        maxWidth="xs"
        fullWidth
        fullScreen={isSmall}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "20px" },
            p: 1,
            m: { xs: 0, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color="#1a1a2e" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>Verify New Device</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, borderRadius: "12px", fontSize: { xs: "0.78rem", sm: "0.875rem" } }}>
            We detected a login from a new device/location. Please enter the OTP sent to your email to verify.
          </Alert>
          <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, justifyContent: "center", mb: 2 }}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={otpRefs[index]}
                value={digit}
                onChange={(e) => handleOTPChange(index, e.target.value)}
                onKeyDown={(e) => handleOTPKeyDown(index, e)}
                inputProps={{ maxLength: 1, style: { textAlign: "center", fontSize: isSmall ? "20px" : "24px", fontWeight: 700 } }}
                sx={{
                  width: { xs: 48, sm: 60 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "& fieldset": { borderColor: digit ? SKY : undefined },
                    "&.Mui-focused fieldset": { borderColor: SKY },
                  },
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", fontSize: { xs: "0.72rem", sm: "0.75rem" } }}>
            Didn't receive the code?{" "}
            <Link onClick={handleResendIPOTP} sx={{ fontWeight: 600, color: SKY_DARK, cursor: "pointer", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
              {loading ? "Sending…" : "Resend OTP"}
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, gap: 1 }}>
          <Button onClick={handleCloseIPDialog} disabled={verifying} variant="outlined"
            sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, flex: 1, borderColor: SKY_DARK, color: SKY_DARK, fontSize: { xs: "0.82rem", sm: "0.875rem" } }}>
            Cancel
          </Button>
          <Button onClick={handleVerifyIP} variant="contained" disabled={verifying || otp.join("").length !== 4}
            sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, flex: 1, background: `linear-gradient(135deg, #2c3e50 0%, ${SKY_DARK} 100%)`, fontSize: { xs: "0.82rem", sm: "0.875rem" } }}>
            {verifying ? "Verifying…" : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotOpen}
        onClose={handleForgotClose}
        maxWidth="xs"
        fullWidth
        fullScreen={isSmall}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: "20px" }, p: 1, m: { xs: 0, sm: 2 } } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color="#1a1a2e" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            {forgotStep === 0 && "Reset Password"}
            {forgotStep === 1 && "Verify OTP"}
            {forgotStep === 2 && "Set New Password"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {forgotError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "12px", fontSize: { xs: "0.78rem", sm: "0.83rem" } }}>
              {forgotError}
            </Alert>
          )}

          {/* Step 0: Enter email */}
          {forgotStep === 0 && (
            <>
              <Typography sx={{ color: "#6b7280", fontSize: { xs: "0.82rem", sm: "0.875rem" }, mb: 2.5 }}>
                Enter your registered email address and we'll send you an OTP to reset your password.
              </Typography>
              <TextField
                fullWidth
                placeholder="you@company.com"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                size="small"
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ fontSize: "1.05rem", color: SKY_DARK }} />
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          {/* Step 1: Enter OTP */}
          {forgotStep === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2.5, borderRadius: "12px", fontSize: { xs: "0.78rem", sm: "0.83rem" } }}>
                We've sent a 4-digit OTP to {forgotEmail}
              </Alert>
              <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, justifyContent: "center", mb: 2 }}>
                {forgotOtp.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={forgotOtpRefs[index]}
                    value={digit}
                    onChange={(e) => handleForgotOTPChange(index, e.target.value)}
                    onKeyDown={(e) => handleForgotOTPKeyDown(index, e)}
                    inputProps={{ maxLength: 1, style: { textAlign: "center", fontSize: isSmall ? "20px" : "24px", fontWeight: 700 } }}
                    sx={{
                      width: { xs: 48, sm: 60 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "& fieldset": { borderColor: digit ? SKY : undefined },
                        "&.Mui-focused fieldset": { borderColor: SKY },
                      },
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", fontSize: { xs: "0.72rem", sm: "0.75rem" } }}>
                Didn't receive the code?{" "}
                <Link onClick={handleSendResetOTP} sx={{ fontWeight: 600, color: SKY_DARK, cursor: "pointer" }}>Resend OTP</Link>
              </Typography>
            </>
          )}

          {/* Step 2: New password */}
          {forgotStep === 2 && (
            <>
              <Typography sx={{ color: "#6b7280", fontSize: { xs: "0.82rem", sm: "0.875rem" }, mb: 2.5 }}>
                Enter your new password below.
              </Typography>
              <TextField
                fullWidth
                placeholder="New password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                size="small"
                sx={{ ...inputSx, mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ fontSize: "1.05rem", color: SKY_DARK }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" tabIndex={-1} size="small" sx={{ color: "#9ca3af" }}>
                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                placeholder="Confirm new password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                size="small"
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ fontSize: "1.05rem", color: SKY_DARK }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" tabIndex={-1} size="small" sx={{ color: "#9ca3af" }}>
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, gap: 1 }}>
          <Button onClick={handleForgotClose} disabled={forgotLoading} variant="outlined"
            sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, flex: 1, borderColor: SKY_DARK, color: SKY_DARK, fontSize: { xs: "0.82rem", sm: "0.875rem" } }}>
            Cancel
          </Button>
          <Button
            onClick={forgotStep === 0 ? handleSendResetOTP : forgotStep === 1 ? handleVerifyResetOTP : handleResetPassword}
            variant="contained"
            disabled={forgotLoading}
            sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, flex: 1, background: `linear-gradient(135deg, #2c3e50 0%, ${SKY_DARK} 100%)`, fontSize: { xs: "0.82rem", sm: "0.875rem" } }}
          >
            {forgotLoading ? "Please wait…" : forgotStep === 0 ? "Send OTP" : forgotStep === 1 ? "Verify OTP" : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick theme="colored" />
    </Box>
  );
};

export default Login;
